import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { firmsApi, type Firm, type FirmBrandingUpdate } from '../lib/api';

// Firm branding for partner client reports (CLARID-SPEC-PBR-001). Firm admins only; the server
// enforces that too. Deliberately not in the sidebar for v1 — reached from the Client report modal.

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const LOGO_TYPES = ['image/png', 'image/jpeg'];
const MAX_ADDRESS_LINES = 4;
const HEX = /^[0-9A-Fa-f]{6}$/;

const inputClass =
  'block w-full rounded-md border border-rule bg-white px-3 py-2 text-sm text-ink placeholder:text-ink-4 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';
const labelClass = 'block text-sm font-medium text-ink-2 mb-1';

export default function FirmBrandingPage() {
  const { data: firms, isLoading, error } = useQuery({
    queryKey: ['firms', 'mine'],
    queryFn: async () => (await firmsApi.mine()).data.firms,
  });

  const adminFirms = (firms ?? []).filter((f) => f.role === 'ADMIN');
  const [selectedId, setSelectedId] = useState('');
  const firm = adminFirms.find((f) => f.id === selectedId) ?? adminFirms[0];

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-ink">Firm branding</h1>
        <p className="mt-1 text-sm text-ink-3">
          Your firm&apos;s name, logo and colour on the client reports your consultants generate.
        </p>
      </div>

      {isLoading ? (
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-panel-2 rounded" />
          <div className="h-64 bg-panel-2 rounded-lg" />
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">Your firms could not be loaded. Refresh to try again.</p>
      ) : !firm ? (
        <div className="bg-panel rounded-lg border border-rule p-6 text-sm text-ink-2">
          Branding is managed by your firm&apos;s admin.
        </div>
      ) : (
        <>
          {adminFirms.length > 1 && (
            <div className="max-w-sm">
              <label htmlFor="fb-firm" className={labelClass}>Firm</label>
              <select id="fb-firm" value={firm.id} onChange={(e) => setSelectedId(e.target.value)} className={inputClass}>
                {adminFirms.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          )}
          {/* Keyed by firm so switching firms starts from that firm's saved values. */}
          <LogoSection key={`logo-${firm.id}`} firm={firm} />
          <BrandingForm key={`form-${firm.id}`} firm={firm} />
        </>
      )}
    </div>
  );
}

// ─── Branding fields ─────────────────────────────────────────────────────────

function BrandingForm({ firm }: { firm: Firm }) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState({
    reportDisplayName: firm.reportDisplayName ?? '',
    legalName: firm.legalName ?? '',
    accentColor: firm.accentColor ?? '',
    address: (firm.addressLines ?? []).join('\n'),
    website: firm.website ?? '',
    defaultSignerName: firm.defaultSignerName ?? '',
    defaultSignerTitle: firm.defaultSignerTitle ?? '',
  });
  const [saving, setSaving] = useState(false);

  const set = (key: keyof typeof values, value: string) => setValues((v) => ({ ...v, [key]: value }));

  const hex = values.accentColor.trim().replace(/^#/, '');
  const hexValid = hex === '' || HEX.test(hex);
  const addressLines = values.address
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const addressValid = addressLines.length <= MAX_ADDRESS_LINES;

  const handleSave = async () => {
    if (saving || !hexValid || !addressValid) return;
    setSaving(true);
    const orNull = (s: string) => s.trim() || null;
    const body: FirmBrandingUpdate = {
      reportDisplayName: orNull(values.reportDisplayName),
      legalName: orNull(values.legalName),
      accentColor: hex ? hex.toUpperCase() : null,
      addressLines,
      website: orNull(values.website),
      defaultSignerName: orNull(values.defaultSignerName),
      defaultSignerTitle: orNull(values.defaultSignerTitle),
    };
    try {
      await firmsApi.updateBranding(firm.id, body);
      await queryClient.invalidateQueries({ queryKey: ['firms', 'mine'] });
      toast.success('Branding saved');
    } catch {
      // The API interceptor already shows the server's message.
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-panel rounded-lg border border-rule p-6">
      <h2 className="text-base font-semibold text-ink">Report details</h2>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field id="fb-display" label="Name in the report" hint="Used in the report's sentences. Blank uses the firm name.">
          <input id="fb-display" className={inputClass} value={values.reportDisplayName} placeholder={firm.name} onChange={(e) => set('reportDisplayName', e.target.value)} />
        </Field>
        <Field id="fb-legal" label="Legal name" hint="Printed in the footer copyright line.">
          <input id="fb-legal" className={inputClass} value={values.legalName} onChange={(e) => set('legalName', e.target.value)} />
        </Field>
        <Field id="fb-color" label="Accent colour" hint="Six-digit hex, used for table headers and headings." error={hexValid ? undefined : 'Use a 6-digit hex colour such as 0F2742.'}>
          <div className="flex items-center gap-2">
            <span
              aria-hidden
              className="h-9 w-9 shrink-0 rounded-md border border-rule"
              style={hex && hexValid ? { backgroundColor: `#${hex}` } : undefined}
            />
            <input
              id="fb-color"
              className={inputClass}
              value={values.accentColor}
              placeholder="0F2742"
              maxLength={7}
              onChange={(e) => set('accentColor', e.target.value)}
            />
          </div>
        </Field>
        <Field id="fb-web" label="Website">
          <input id="fb-web" className={inputClass} value={values.website} placeholder="example.com" onChange={(e) => set('website', e.target.value)} />
        </Field>
        <Field
          id="fb-address"
          label="Address"
          hint={`Printed on the cover. One line per row, up to ${MAX_ADDRESS_LINES}.`}
          error={addressValid ? undefined : `Use at most ${MAX_ADDRESS_LINES} address lines.`}
        >
          <textarea id="fb-address" rows={4} className={inputClass} value={values.address} onChange={(e) => set('address', e.target.value)} />
        </Field>
        <div className="space-y-4">
          <Field id="fb-signer" label="Default signer name" hint="Prefilled on each report's cover letter.">
            <input id="fb-signer" className={inputClass} value={values.defaultSignerName} onChange={(e) => set('defaultSignerName', e.target.value)} />
          </Field>
          <Field id="fb-signer-title" label="Default signer title">
            <input id="fb-signer-title" className={inputClass} value={values.defaultSignerTitle} onChange={(e) => set('defaultSignerTitle', e.target.value)} />
          </Field>
        </div>
      </div>
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving || !hexValid || !addressValid}
          className="rounded-md px-4 py-2 text-sm font-medium bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </section>
  );
}

// ─── Logo ────────────────────────────────────────────────────────────────────

function LogoSection({ firm }: { firm: Firm }) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [loadingLogo, setLoadingLogo] = useState(firm.hasLogo);
  const [version, setVersion] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  // The logo endpoint needs the auth header, so fetch the bytes and show them through an object URL.
  useEffect(() => {
    if (!firm.hasLogo && version === 0) {
      setLoadingLogo(false);
      return;
    }
    let cancelled = false;
    let url: string | null = null;
    setLoadingLogo(true);
    firmsApi
      .getLogo(firm.id)
      .then((res) => {
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setLogoUrl(url);
      })
      .catch(() => {
        if (!cancelled) setLogoUrl(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingLogo(false);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [firm.id, firm.hasLogo, version]);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setFileError(null);
    if (!LOGO_TYPES.includes(file.type)) {
      setFileError('Choose a PNG or JPEG file.');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setFileError(`That file is ${(file.size / (1024 * 1024)).toFixed(1)} MB. The logo must be 2 MB or smaller.`);
      return;
    }
    setUploading(true);
    try {
      await firmsApi.uploadLogo(firm.id, file);
      setVersion((v) => v + 1);
      await queryClient.invalidateQueries({ queryKey: ['firms', 'mine'] });
      toast.success('Logo uploaded');
    } catch {
      // The API interceptor already shows the server's message.
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <section className="bg-panel rounded-lg border border-rule p-6">
      <h2 className="text-base font-semibold text-ink">Logo</h2>
      <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex h-24 w-56 shrink-0 items-center justify-center rounded-md border border-rule bg-white p-2">
          {loadingLogo ? (
            <span className="text-xs text-ink-4">Loading…</span>
          ) : logoUrl ? (
            <img src={logoUrl} alt={`${firm.name} logo`} className="max-h-full max-w-full object-contain" />
          ) : (
            <span className="text-xs text-ink-4">No logo yet</span>
          )}
        </div>
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium border border-rule text-ink-2 hover:bg-panel-2 disabled:opacity-50"
          >
            {uploading ? 'Uploading…' : logoUrl ? 'Replace logo' : 'Upload logo'}
          </button>
          <p className="mt-2 text-xs text-ink-4">
            PNG or JPEG, up to 2 MB. It appears on the cover and in the page header of client reports.
          </p>
          {fileError && (
            <p className="mt-1 text-sm text-red-600" role="alert">
              {fileError}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({
  id,
  label,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      {children}
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : hint && <p className="mt-1 text-xs text-ink-4">{hint}</p>}
    </div>
  );
}
